"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslations } from "next-intl";
import { useId, useState } from "react";
import { Controller, type SubmitHandler, useForm } from "react-hook-form";

import type {
	SnackbarCloseReason,
	SnackbarOrigin,
} from "@/components/controls/base-controls";
import {
	Box,
	Button,
	IconButton,
	Snackbar,
	SnackbarContent,
	Stack,
	TextField,
	Typography,
	useMediaQuery,
	useTheme
} from "@/components/controls/base-controls";
import { CloseIcon } from "@/components/controls/icons";
import { unwrapDal, DalMutationError } from "@/core/dal/unwrap";
import type { SubscriberFormData } from "@/core/schemas/subscriber.form.schema";
import { subscriberFormSchema } from "@/core/schemas/subscriber.form.schema";
import ServiceRegistry from "@/core/services/service-registry";
import SubscribersService from "@/core/services/subscribers.service";
import { useLoadingStore } from "@/core/utils/loading.store";

interface SnackbarState extends SnackbarOrigin {
	open: boolean;
}

const SubscriberForm = () => {
	const t = useTranslations();

	const { isLoading } = useLoadingStore();

	const theme = useTheme();
	// Returns true on mobile (xs, sm), false on desktop (md, lg, xl)
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));

	const [openSnackbar, setOpenSnackbar] = useState<SnackbarState>({
		open: false,
		vertical: "top",
		horizontal: "center",
	});

	const { vertical, horizontal, open } = openSnackbar;

	const emailId = useId();

	const {
		control,
		handleSubmit,
		formState: { errors },
		reset,
	} = useForm<SubscriberFormData>({
		mode: "onSubmit",
		resolver: zodResolver(subscriberFormSchema),
		defaultValues: {
			email: "",
			active: true,
		},
	});

	const onSubmit: SubmitHandler<SubscriberFormData> = async (data) => {
		try {
			const subscribersService = ServiceRegistry.get(SubscribersService);

			// Unwrap the DalReturn: resolves on success, throws DalMutationError on failure
			unwrapDal(await subscribersService.submitSubscriber(data));

			setOpenSnackbar({ vertical: "top", horizontal: "center", open: true });

			reset({ email: "", active: true });
		} catch (error) {
			console.error("Subscriber addition failed:", error);
			// Surface the DAL error via its i18n key, falling back to the generic failure message
			alert(
				error instanceof DalMutationError
					? t(error.i18nKey)
					: t("subscriber.submission.subscribeFailure"),
			);
		}
	};

	const handleSnackbarClose = (
		_event: React.SyntheticEvent | Event,
		reason?: SnackbarCloseReason,
	) => {
		if (reason === "clickaway") {
			return; // Prevent closing on clickaway
		}
		setOpenSnackbar({ ...openSnackbar, open: false }); // Close only when explicitly triggered or other reasons
	};

	return (
		<>
			<Stack>
				<Typography
					variant="h3"
					sx={{
						color: "var(--mui-palette-bright)",
					}}
				>
					{t("subscriber.submission.subscribe")}
				</Typography>

				<Typography
					variant="body1"
					sx={{
						color: "var(--mui-palette-bright)",
						fontSize: { xs: ".875rem", md: "1rem" },
						lineHeight: "2.5em",
					}}
				>
					{t("subscriber.submission.subscriptionSubtitle")}
				</Typography>

				<Box
					component="form"
					noValidate 
					aria-busy={isLoading} aria-describedby="data-processing"
					onSubmit={handleSubmit(onSubmit)}>
					<Box
						sx={{
							display: "flex",
							flexWrap: "wrap",
							justifyContent: "center",
							alignItems: "flex-start",
							gap: ".5em",
							margin: ".5em 0",
							width: "100%",
						}}
					>
						<Controller
							name="email"
							control={control}
							render={({ field }) => (
								<TextField
									{...field}
									id={emailId}
									type="email"
									inputMode="email"
									label={t("email.label")}
									error={!!errors.email}
									helperText={
										errors?.email?.message && t(errors.email.message as string)
									}
									slotProps={{ 
										htmlInput: { maxLength: 255 }, 
										inputLabel: { shrink: isMobile ? true : undefined }
									}}
									variant="filled"
									sx={{
										width: { xs: "14em", md: "16em" },
									}}
								/>
							)}
						/>

						<Button
							variant="lightBg"
							sx={{
								padding: "1.2em 2.2em",
							}}
							type="submit"
							disabled={isLoading}
						>
							{t("subscriber.submission.submit")}
						</Button>
					</Box>
				</Box>
			</Stack>

			<Snackbar
				anchorOrigin={{ vertical, horizontal }}
				open={open}
				onClose={handleSnackbarClose}
				key={vertical + horizontal}
				autoHideDuration={null}
			>
				<SnackbarContent
					message={t("subscriber.submission.success")}
					sx={{ backgroundColor: "var(--mui-palette-success)" }}
					action={
						<IconButton
							size="small"
							aria-label="close"
							color="inherit"
							onClick={handleSnackbarClose}
						>
							<CloseIcon fontSize="small" />
						</IconButton>
					}
				/>
			</Snackbar>
		</>
	);
};

export default SubscriberForm;
